import { useState } from 'react';
import { Link, useLoaderData, useNavigate } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';

import { Badge, Button, Card, Icon } from '@ds/core';
import { DataTable } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Tabs } from '@ds/navigation';
import { PageHeader } from '@/comuns/PageHeader';
import { dataSimples } from '@/comuns/formato';
import { emRisco } from '@/data/plataformaDaVecchia';
import { dados } from '@/data/porta';
import type { AbaDeClinicas, CargaDaPlataforma, ClinicaNaPlataforma } from '@/data/tipos';
import { rotaPorId } from '@/router/rotas';
import { pode } from '@/session/sessao';
import { CriarClinicaModal } from './CriarClinicaModal';
import { SITUACAO_DA_CLINICA, quandoRelativo } from './Visao';

const ABAS: AbaDeClinicas[] = ['todas', 'trial', 'risco', 'inadimplentes', 'canceladas'];

function abaDaRota(pathname: string): AbaDeClinicas {
    const ultimo = pathname.split('/').pop() ?? '';
    return (ABAS as string[]).includes(ultimo) ? (ultimo as AbaDeClinicas) : 'todas';
}

export async function carregarClinicas({ request }: LoaderFunctionArgs): Promise<CargaDaPlataforma & { aba: AbaDeClinicas }> {
    const carga = await dados().plataforma();
    return { ...carga, aba: abaDaRota(new URL(request.url).pathname) };
}

/** "Em risco" é derivado do uso, não uma coluna: gravado, ficaria preso. */
function recortar(clinicas: ClinicaNaPlataforma[], aba: AbaDeClinicas): ClinicaNaPlataforma[] {
    if (aba === 'trial') return clinicas.filter((c) => c.situacao === 'trial');
    if (aba === 'inadimplentes') return clinicas.filter((c) => c.situacao === 'inadimplente');
    if (aba === 'canceladas') return clinicas.filter((c) => c.situacao === 'cancelada');
    if (aba === 'risco') return clinicas.filter((c) => emRisco(c));
    return clinicas;
}

export function Clinicas() {
    const carga = useLoaderData() as CargaDaPlataforma & { aba: AbaDeClinicas };
    const navigate = useNavigate();
    const [criando, setCriando] = useState(false);
    const rota = rotaPorId('clinicas');
    const lista = recortar(carga.clinicas, carga.aba);

    const colunas: DataTableColumn<ClinicaNaPlataforma>[] = [
        {
            key: 'nome', header: 'Clínica', sortable: true,
            render: (c) => (
                <span>
                    <Link to={`/admin/clinicas/${c.id}`} style={{ display: 'block', color: 'var(--text-primary)', fontWeight: 600, textDecoration: 'none' }}>
                        {c.nome}
                    </Link>
                    <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{c.apelido} · plano {c.plano}</span>
                </span>
            ),
        },
        {
            key: 'situacao', header: 'Situação', width: 140,
            render: (c) => <Badge tone={SITUACAO_DA_CLINICA[c.situacao].tom} dot>{SITUACAO_DA_CLINICA[c.situacao].rotulo}</Badge>,
        },
        { key: 'membrosAtivos', header: 'Pessoas', align: 'right', width: 90 },
        { key: 'contatos', header: 'Contatos', align: 'right', width: 100 },
        { key: 'agendamentos30d', header: 'Agenda 30d', align: 'right', width: 120 },
        {
            key: 'criadoEm', header: 'Cliente desde', align: 'right', width: 140,
            render: (c) => dataSimples(c.criadoEm.slice(0, 10)),
        },
        {
            key: 'ultimoAcesso', header: 'Último acesso', align: 'right', width: 140,
            render: (c) => <span style={{ color: c.ultimoAcesso ? 'var(--text-secondary)' : 'var(--danger)' }}>{quandoRelativo(c.ultimoAcesso)}</span>,
        },
    ];

    const contagem = (aba: AbaDeClinicas) => recortar(carga.clinicas, aba).length;

    return (
        <div className="vec-screen" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <PageHeader
                title="Clínicas"
                description="A base de clientes, com o uso de cada um."
                actions={pode('clientes.editar') ? (
                    <Button iconLeft={<Icon name="plus" size={15} />} onClick={() => setCriando(true)}>Nova clínica</Button>
                ) : undefined}
            >
                <Tabs
                    ariaLabel="Recortes da base" activeId={carga.aba}
                    onChange={(id) => navigate(`/admin/clinicas/${id}`)}
                    tabs={(rota?.children ?? []).map((c) => ({
                        id: c.path,
                        label: c.label,
                        count: contagem(c.path as AbaDeClinicas),
                    }))}
                />
            </PageHeader>

            <Card padding={0}>
                <DataTable
                    columns={colunas} rows={lista} rowKey="id"
                    stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                    emptyMessage="Nenhuma clínica neste recorte."
                />
            </Card>

            <CriarClinicaModal aberto={criando} onFechar={() => setCriando(false)} />
        </div>
    );
}
